// src/components/UserProfilePage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { familyService, memberService } from '../services';

import MemberPhotoAvatar from './MemberPhotoAvatar';
import FormattedPhoneNumber from './FormattedPhoneNumber';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faShieldAlt,
    faCheckCircle,
    faExclamationTriangle,
    faSpinner,
    faCamera,
    faBell,
    faUserPlus,
    faCake,
    faCalendarAlt,
    faMobileAlt,
    faUsers,
    faPencilAlt,
    faMapMarkerAlt,
    faPhone,
    faHeart,
    faEnvelope,
    faBirthdayCake,
    faExternalLinkAlt,
    faImages
} from '@fortawesome/free-solid-svg-icons';
import { faEnvelope as faEnvelopeRegular } from '@fortawesome/free-regular-svg-icons';
import { faApple, faAndroid } from '@fortawesome/free-brands-svg-icons';
import { Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

import './UserProfilePage.css';

function UserProfilePage() {
    const { currentUser, isAdmin, memberPhoto, memberName, userFidClaim, userMidClaim } = useAuth();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Notification preferences state
    const [notificationPrefs, setNotificationPrefs] = useState({
        newMemberNotifications: true,
        birthdayAnniversaryNotifications: true,
        messageNotifications: true,
        eventNotifications: true
    });
    const [savingPrefs, setSavingPrefs] = useState({});
    const [prefsSuccess, setPrefsSuccess] = useState({});

    // Device tokens state
    const [deviceTokens, setDeviceTokens] = useState([]);
    const [loadingTokens, setLoadingTokens] = useState(true);

    // My Family state
    const [familyData, setFamilyData] = useState(null);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [loadingFamily, setLoadingFamily] = useState(true);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const user = auth.currentUser;

        if (user) {
            setUserData({
                email: user.email,
                uid: user.uid,
                emailVerified: user.emailVerified,
                creationTime: user.metadata.creationTime,
                lastSignInTime: user.metadata.lastSignInTime,
            });

            user.getIdTokenResult(true)
                .then(() => {
                    setError(null);
                })
                .catch((claimError) => {
                    console.error("Error fetching user claims:", claimError);
                    setError("Could not load user permissions. Please try refreshing.");
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setError("No user is currently logged in.");
            setLoading(false);
        }
    }, []);

    // Fetch user notification preferences
    useEffect(() => {
        const fetchUserPrefs = async () => {
            const user = auth.currentUser;
            if (!user) return;

            try {
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const data = userDocSnap.data();
                    if (data.notificationPreferences) {
                        setNotificationPrefs({
                            newMemberNotifications: data.notificationPreferences.newMemberNotifications ?? true,
                            birthdayAnniversaryNotifications: data.notificationPreferences.birthdayAnniversaryNotifications ?? true,
                            messageNotifications: data.notificationPreferences.messageNotifications ?? true,
                            eventNotifications: data.notificationPreferences.eventNotifications ?? true
                        });
                    }
                }
            } catch (err) {
                console.error("Error fetching user preferences:", err);
            }
        };

        fetchUserPrefs();
    }, []);

    // Fetch device tokens
    useEffect(() => {
        const fetchDeviceTokens = async () => {
            const user = auth.currentUser;
            if (!user) {
                setLoadingTokens(false);
                return;
            }

            try {
                const tokensCollectionRef = collection(db, 'users', user.uid, 'tokens');
                const tokensSnap = await getDocs(tokensCollectionRef);

                const tokens = tokensSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setDeviceTokens(tokens);
            } catch (err) {
                console.error("Error fetching device tokens:", err);
            } finally {
                setLoadingTokens(false);
            }
        };

        fetchDeviceTokens();
    }, []);

    // Fetch user's family and members
    useEffect(() => {
        if (!userFidClaim) {
            setLoadingFamily(false);
            return;
        }

        let isMounted = true;

        const fetchFamily = async () => {
            try {
                const fam = await familyService.getFamily(userFidClaim);
                if (!isMounted) return;

                if (fam) {
                    // Resolve family photo URL
                    if (fam.photo) {
                        if (fam.photo.startsWith('http')) {
                            fam.resolvedPhotoUrl = fam.photo;
                        } else {
                            try {
                                const url = await familyService.getFamilyPhotoUrl(fam.photo, fam.imageUpdatedAt);
                                if (url) fam.resolvedPhotoUrl = url;
                            } catch (e) {
                                console.error('Error resolving family photo:', e);
                            }
                        }
                    }
                    setFamilyData(fam);

                    // Fetch members
                    try {
                        const members = await memberService.getMembersByFamily(userFidClaim);
                        if (isMounted) {
                            setFamilyMembers(members.filter(m => m.status !== 'deceased'));
                        }
                    } catch (e) {
                        console.error('Error fetching family members:', e);
                    }
                }
            } catch (err) {
                console.error('Error fetching family:', err);
            } finally {
                if (isMounted) setLoadingFamily(false);
            }
        };

        fetchFamily();
        return () => { isMounted = false; };
    }, [userFidClaim]);

    // Handle notification preference toggle
    const handlePreferenceToggle = async (prefKey) => {
        const user = auth.currentUser;
        if (!user) return;

        const newValue = !notificationPrefs[prefKey];

        // Optimistic update
        setNotificationPrefs(prev => ({ ...prev, [prefKey]: newValue }));
        setSavingPrefs(prev => ({ ...prev, [prefKey]: true }));
        setPrefsSuccess(prev => ({ ...prev, [prefKey]: false }));

        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                [`notificationPreferences.${prefKey}`]: newValue
            });
            setPrefsSuccess(prev => ({ ...prev, [prefKey]: true }));
            setTimeout(() => setPrefsSuccess(prev => ({ ...prev, [prefKey]: false })), 2000);
        } catch (err) {
            console.error("Error updating preference:", err);
            // Revert on error
            setNotificationPrefs(prev => ({ ...prev, [prefKey]: !newValue }));
            setError("Failed to update preference. Please try again.");
        } finally {
            setSavingPrefs(prev => ({ ...prev, [prefKey]: false }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
            });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Format family address
    const formatAddress = (family) => {
        const parts = [];
        if (family.street) parts.push(family.street);
        const cityStateZip = [family.city, family.state, family.zipcode].filter(Boolean).join(family.city && family.state ? ', ' : ' ');
        if (cityStateZip) parts.push(cityStateZip);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    // Format birthday from member fields
    const formatBirthday = (member) => {
        if (member?.birthdayMonth && member?.birthdayDay) {
            const month = parseInt(member.birthdayMonth, 10);
            const day = parseInt(member.birthdayDay, 10);
            const dateObj = new Date(2000, month - 1, day);
            if (!isNaN(dateObj.getTime()) && dateObj.getMonth() === month - 1) {
                return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
        }
        return null;
    };

    // Format anniversary
    const formatAnniversary = (family) => {
        if (family.anniversaryMonth && family.anniversaryDay) {
            const month = parseInt(family.anniversaryMonth, 10);
            const day = parseInt(family.anniversaryDay, 10);
            const dateObj = new Date(2000, month - 1, day);
            if (!isNaN(dateObj.getTime()) && dateObj.getMonth() === month - 1) {
                return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }
        }
        return null;
    };

    // Get first letter of email for avatar
    const getInitial = () => {
        if (userData?.email) {
            return userData.email.charAt(0).toUpperCase();
        }
        return '?';
    };

    // Loading state
    if (loading) {
        return (
            <div className="profile-page">
                <div className="profile-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-card"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    // Error state with no user
    if (error && !currentUser) {
        return (
            <div className="profile-page">
                <div className="profile-header">
                    <div className="container">
                        <div className="profile-header-content">
                            <h1 className="profile-title">My Profile</h1>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="profile-content">
                        <Alert variant="danger">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            {error}
                        </Alert>
                        <Link to="/login" className="profile-action-btn profile-action-btn-primary">
                            Go to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            {/* Page Header */}
            <div className="profile-header">
                <div className="container">
                    <div className="profile-header-content">
                        <div>
                            <h1 className="profile-title">My Profile</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="profile-content">
                    {error && (
                        <Alert variant="warning" className="mb-3" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}

                    {/* Account Information Card */}
                    <div className="profile-card">
                        <div className="profile-card-header">
                            <div className="profile-card-icon profile-card-icon-user">
                                <FontAwesomeIcon icon={faUser} />
                            </div>
                            <h2 className="profile-card-title">Account Information</h2>
                        </div>
                        <div className="profile-card-body">
                            {userData && (
                                <>
                                    <div className="profile-avatar-section">
                                        <div className="profile-avatar-photo-wrapper">
                                            {memberPhoto ? (
                                                <MemberPhotoAvatar src={memberPhoto} alt="Profile" size={64} />
                                            ) : (
                                                <div className="profile-avatar">
                                                    {getInitial()}
                                                </div>
                                            )}
                                            {userFidClaim && (
                                                <Link
                                                    to={`/families/${userFidClaim}/assign-photos`}
                                                    state={{
                                                        forMemberName: memberName || currentUser?.displayName || userData?.email,
                                                        forMemberId: userMidClaim || null,
                                                        currentPhotoPath: memberPhoto || null,
                                                        returnPath: '/profile'
                                                    }}
                                                    className="profile-change-photo-link"
                                                    title="Change photo"
                                                >
                                                    <FontAwesomeIcon icon={faCamera} /> Change Photo
                                                </Link>
                                            )}
                                        </div>
                                        <div className="profile-avatar-info">
                                            <h3>{userData.email}</h3>
                                            <p>
                                                {isAdmin && (
                                                    <span className="profile-badge profile-badge-admin">
                                                        <FontAwesomeIcon icon={faShieldAlt} /> Admin
                                                    </span>
                                                )}
                                                {userData.emailVerified ? (
                                                    <span className="profile-badge profile-badge-verified ms-1">
                                                        <FontAwesomeIcon icon={faCheckCircle} /> Verified
                                                    </span>
                                                ) : (
                                                    <span className="profile-badge profile-badge-unverified ms-1">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} /> Unverified
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="profile-info-row">
                                        <span className="profile-info-label">Email Address</span>
                                        <span className="profile-info-value">{userData.email}</span>
                                    </div>
                                    <div className="profile-info-row">
                                        <span className="profile-info-label">Email Status</span>
                                        <span className={`profile-info-value ${userData.emailVerified ? 'verified' : 'not-verified'}`}>
                                            {userData.emailVerified ? 'Verified' : 'Not Verified'}
                                        </span>
                                    </div>
                                    <div className="profile-info-row">
                                        <span className="profile-info-label">Account Created</span>
                                        <span className="profile-info-value">{formatDate(userData.creationTime)}</span>
                                    </div>
                                    <div className="profile-info-row">
                                        <span className="profile-info-label">Last Sign In</span>
                                        <span className="profile-info-value">{formatDateTime(userData.lastSignInTime)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Preferences Card */}
                    <div className="profile-card">
                        <div className="profile-card-header">
                            <div className="profile-card-icon profile-card-icon-prefs">
                                <FontAwesomeIcon icon={faBell} />
                            </div>
                            <h2 className="profile-card-title">Preferences</h2>
                        </div>
                        <div className="profile-card-body">
                            <p className="profile-prefs-description">
                                Choose which notifications you'd like to receive.
                            </p>

                            {/* New Member Notifications */}
                            <div className="profile-pref-item">
                                <div className="profile-pref-info">
                                    <div className="profile-pref-icon">
                                        <FontAwesomeIcon icon={faUserPlus} />
                                    </div>
                                    <div className="profile-pref-text">
                                        <span className="profile-pref-title">New Member Added</span>
                                        <span className="profile-pref-desc">Get notified when new members join the directory</span>
                                    </div>
                                </div>
                                <div className="profile-pref-toggle-wrapper">
                                    {savingPrefs.newMemberNotifications && (
                                        <FontAwesomeIcon icon={faSpinner} spin className="profile-pref-spinner" />
                                    )}
                                    {prefsSuccess.newMemberNotifications && (
                                        <FontAwesomeIcon icon={faCheckCircle} className="profile-pref-success" />
                                    )}
                                    <label className="profile-toggle">
                                        <input
                                            type="checkbox"
                                            checked={notificationPrefs.newMemberNotifications}
                                            onChange={() => handlePreferenceToggle('newMemberNotifications')}
                                            disabled={savingPrefs.newMemberNotifications}
                                        />
                                        <span className="profile-toggle-slider"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Birthday & Anniversary Notifications */}
                            <div className="profile-pref-item">
                                <div className="profile-pref-info">
                                    <div className="profile-pref-icon profile-pref-icon-birthday">
                                        <FontAwesomeIcon icon={faCake} />
                                    </div>
                                    <div className="profile-pref-text">
                                        <span className="profile-pref-title">Birthdays & Anniversaries</span>
                                        <span className="profile-pref-desc">Receive reminders for upcoming celebrations</span>
                                    </div>
                                </div>
                                <div className="profile-pref-toggle-wrapper">
                                    {savingPrefs.birthdayAnniversaryNotifications && (
                                        <FontAwesomeIcon icon={faSpinner} spin className="profile-pref-spinner" />
                                    )}
                                    {prefsSuccess.birthdayAnniversaryNotifications && (
                                        <FontAwesomeIcon icon={faCheckCircle} className="profile-pref-success" />
                                    )}
                                    <label className="profile-toggle">
                                        <input
                                            type="checkbox"
                                            checked={notificationPrefs.birthdayAnniversaryNotifications}
                                            onChange={() => handlePreferenceToggle('birthdayAnniversaryNotifications')}
                                            disabled={savingPrefs.birthdayAnniversaryNotifications}
                                        />
                                        <span className="profile-toggle-slider"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Message Notifications */}
                            <div className="profile-pref-item">
                                <div className="profile-pref-info">
                                    <div className="profile-pref-icon profile-pref-icon-message">
                                        <FontAwesomeIcon icon={faEnvelopeRegular} />
                                    </div>
                                    <div className="profile-pref-text">
                                        <span className="profile-pref-title">Messages</span>
                                        <span className="profile-pref-desc">Get notified when you receive new messages</span>
                                    </div>
                                </div>
                                <div className="profile-pref-toggle-wrapper">
                                    {savingPrefs.messageNotifications && (
                                        <FontAwesomeIcon icon={faSpinner} spin className="profile-pref-spinner" />
                                    )}
                                    {prefsSuccess.messageNotifications && (
                                        <FontAwesomeIcon icon={faCheckCircle} className="profile-pref-success" />
                                    )}
                                    <label className="profile-toggle">
                                        <input
                                            type="checkbox"
                                            checked={notificationPrefs.messageNotifications}
                                            onChange={() => handlePreferenceToggle('messageNotifications')}
                                            disabled={savingPrefs.messageNotifications}
                                        />
                                        <span className="profile-toggle-slider"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Event Notifications */}
                            <div className="profile-pref-item">
                                <div className="profile-pref-info">
                                    <div className="profile-pref-icon profile-pref-icon-event">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                    </div>
                                    <div className="profile-pref-text">
                                        <span className="profile-pref-title">Events</span>
                                        <span className="profile-pref-desc">Stay updated on upcoming events</span>
                                    </div>
                                </div>
                                <div className="profile-pref-toggle-wrapper">
                                    {savingPrefs.eventNotifications && (
                                        <FontAwesomeIcon icon={faSpinner} spin className="profile-pref-spinner" />
                                    )}
                                    {prefsSuccess.eventNotifications && (
                                        <FontAwesomeIcon icon={faCheckCircle} className="profile-pref-success" />
                                    )}
                                    <label className="profile-toggle">
                                        <input
                                            type="checkbox"
                                            checked={notificationPrefs.eventNotifications}
                                            onChange={() => handlePreferenceToggle('eventNotifications')}
                                            disabled={savingPrefs.eventNotifications}
                                        />
                                        <span className="profile-toggle-slider"></span>
                                    </label>
                                </div>
                            </div>

                            {/* Mobile Device Status */}
                            <div className="profile-device-section">
                                <div className="profile-device-header">
                                    <FontAwesomeIcon icon={faMobileAlt} className="profile-device-icon" />
                                    <span>Mobile App</span>
                                </div>
                                {loadingTokens ? (
                                    <div className="profile-device-loading">
                                        <FontAwesomeIcon icon={faSpinner} spin /> Checking connected devices...
                                    </div>
                                ) : deviceTokens.length > 0 ? (
                                    <div className="profile-device-connected">
                                        <FontAwesomeIcon icon={faCheckCircle} className="profile-device-connected-icon" />
                                        <div className="profile-device-connected-text">
                                            <span className="profile-device-connected-title">
                                                {deviceTokens.length} device{deviceTokens.length !== 1 ? 's' : ''} connected
                                            </span>
                                            <span className="profile-device-connected-desc">
                                                You'll receive push notifications on your mobile {deviceTokens.length !== 1 ? 'devices' : 'device'}.
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="profile-device-not-connected">
                                        <div className="profile-device-cta">
                                            <p>Get push notifications on your phone by downloading the Church Aware app.</p>
                                            <div className="profile-device-app-links">
                                                <a
                                                    href="https://apps.apple.com/us/app/church-aware/id1422942877?ls=1"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="profile-app-store-link"
                                                >
                                                    <FontAwesomeIcon icon={faApple} /> App Store
                                                </a>
                                                <a
                                                    href="https://play.google.com/store/apps/details?id=com.churchaware.churchaware"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="profile-app-store-link profile-app-store-link-android"
                                                >
                                                    <FontAwesomeIcon icon={faAndroid} /> Google Play
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* My Family Card */}
                    {userFidClaim && (
                        <div className="profile-card">
                            <div className="profile-card-header">
                                <div className="profile-card-icon profile-card-icon-family">
                                    <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <h2 className="profile-card-title">My Family</h2>
                                {familyData && (
                                    <Link
                                        to={`/families/${userFidClaim}`}
                                        className="profile-family-view-link"
                                        title="View full family page"
                                    >
                                        <FontAwesomeIcon icon={faExternalLinkAlt} /> View Full Page
                                    </Link>
                                )}
                            </div>
                            <div className="profile-card-body">
                                {loadingFamily ? (
                                    <div className="profile-family-loading">
                                        <FontAwesomeIcon icon={faSpinner} spin /> Loading family...
                                    </div>
                                ) : !familyData ? (
                                    <div className="profile-family-empty">
                                        <p>No family record is linked to your account.</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Family Info Summary */}
                                        <div className="profile-family-summary">
                                            {familyData.resolvedPhotoUrl && (
                                                <Link to={`/families/${userFidClaim}`} className="profile-family-photo-link">
                                                    <img
                                                        src={familyData.resolvedPhotoUrl}
                                                        alt={`${familyData.surname || 'Family'}`}
                                                        className="profile-family-photo"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                </Link>
                                            )}
                                            <div className="profile-family-info">
                                                <h3 className="profile-family-name">
                                                    <Link to={`/families/${userFidClaim}`}>
                                                        {familyData.surname || 'My'} Family
                                                    </Link>
                                                </h3>
                                                <div className="profile-family-details">
                                                    {formatAddress(familyData) && (
                                                        <div className="profile-family-detail">
                                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="profile-family-detail-icon" />
                                                            <span>{formatAddress(familyData)}</span>
                                                        </div>
                                                    )}
                                                    {familyData.phone && (
                                                        <div className="profile-family-detail">
                                                            <FontAwesomeIcon icon={faPhone} className="profile-family-detail-icon" />
                                                            <a href={`tel:${familyData.phone}`}>
                                                                <FormattedPhoneNumber phoneNumber={familyData.phone} />
                                                            </a>
                                                        </div>
                                                    )}
                                                    {formatAnniversary(familyData) && (
                                                        <div className="profile-family-detail">
                                                            <FontAwesomeIcon icon={faHeart} className="profile-family-detail-icon" />
                                                            <span>Anniversary: {formatAnniversary(familyData)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Family Actions */}
                                        <div className="profile-family-actions">
                                            <Link
                                                to={`/families/edit/${userFidClaim}`}
                                                className="profile-family-action-btn"
                                            >
                                                <FontAwesomeIcon icon={faPencilAlt} /> Edit Family
                                            </Link>
                                            <Link
                                                to={`/families/${userFidClaim}/assign-photos/all`}
                                                className="profile-family-action-btn"
                                            >
                                                <FontAwesomeIcon icon={faImages} /> Manage Photos
                                            </Link>
                                        </div>

                                        {/* Members List */}
                                        {familyMembers.length > 0 && (
                                            <div className="profile-family-members">
                                                <div className="profile-family-members-header">
                                                    <span>Family Members ({familyMembers.length})</span>
                                                </div>
                                                <div className="profile-family-members-list">
                                                    {familyMembers.map(member => {
                                                        const birthday = formatBirthday(member);
                                                        const photoCacheKey = member.imageUpdatedAt?.seconds
                                                            ? (member.imageUpdatedAt.seconds * 1000).toString()
                                                            : (member.imageUpdatedAt || member.id);

                                                        return (
                                                            <div key={member.id} className="profile-family-member">
                                                                <Link to={`/families/${userFidClaim}/member/${member.id}`} className="profile-family-member-photo">
                                                                    <MemberPhotoAvatar
                                                                        src={member.photo}
                                                                        alt={member.name || 'Member'}
                                                                        size={44}
                                                                        cacheKey={photoCacheKey}
                                                                    />
                                                                </Link>
                                                                <div className="profile-family-member-info">
                                                                    <Link to={`/families/${userFidClaim}/member/${member.id}`} className="profile-family-member-name">
                                                                        {member.name || 'No Name'}
                                                                    </Link>
                                                                    <div className="profile-family-member-details">
                                                                        {birthday && (
                                                                            <span className="profile-family-member-detail">
                                                                                <FontAwesomeIcon icon={faBirthdayCake} /> {birthday}
                                                                            </span>
                                                                        )}
                                                                        {member.cellphone && (
                                                                            <span className="profile-family-member-detail">
                                                                                <FontAwesomeIcon icon={faPhone} /> <FormattedPhoneNumber phoneNumber={member.cellphone} />
                                                                            </span>
                                                                        )}
                                                                        {member.email && (
                                                                            <span className="profile-family-member-detail">
                                                                                <FontAwesomeIcon icon={faEnvelope} /> {member.email}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Link
                                                                    to={`/members/edit/${member.id}`}
                                                                    className="profile-family-member-edit"
                                                                    title={`Edit ${member.name || 'member'}`}
                                                                >
                                                                    <FontAwesomeIcon icon={faPencilAlt} />
                                                                </Link>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default UserProfilePage;
